/**
 * What a press on a roster row means, and which presses belong to the finger.
 *
 * A mouse has two buttons, so the right one opens the row's menu and the left
 * one drags. A finger has neither, so one long press has to serve both: the
 * row lifts under the finger, carried it drops where it is let go, let go
 * where it is it opens the menu — a phone's home screen, and the prototype's
 * roster.
 *
 * The catch is that Android raises a `contextmenu` event from that same long
 * press. A row that opens its menu from that event does both things at once,
 * and the menu's backdrop then lies over the rail and the sections, so the
 * drag's `elementFromPoint` finds the film instead of a drop target and the
 * row lands nowhere. So the finger's press is the lift's alone.
 */

/** How long a finger rests on a row before it lifts. */
export const LONG_PRESS_MS = 500;
/** How far a finger may wander during that press before it is a scroll instead. */
export const PRESS_SLOP_PX = 10;
/**
 * A screen with no pointer to hover with: a phone, or a tablet. The browser's
 * own drag never fires from a finger there, while an element still marked for
 * it lets Android start a drag of its own — which the phone offers to share
 * with another app rather than to move within this one.
 */
export const TOUCH_QUERY = "(hover: none)";

export interface Point {
  readonly x: number;
  readonly y: number;
}

/**
 * Whether a `contextmenu` event should open the row's menu, given what raised
 * the press before it. A mouse's right click and a pen's should; a finger's
 * should not, because the lift owns it and opens the menu itself. Nothing at
 * all — a keyboard's menu key, which no pointer event precedes — is a menu.
 */
export function menuOpensFrom(pointerType: string | null): boolean {
  return pointerType !== "touch";
}

/** Whether the finger has left where it started: carrying the row, or scrolling, rather than resting on it. */
export function wandered(from: Point, to: Point, slop: number = PRESS_SLOP_PX): boolean {
  return Math.hypot(to.x - from.x, to.y - from.y) > slop;
}
